import { useState } from 'react';
import * as api from '../api/storageApi';
import type { DriveFile } from '../types';

interface Props {
  file: DriveFile;
  onClose: () => void;
}

type LinkMode = 'permanent' | 'temporary';

const TEMPORARY_DAYS = 7;

export default function ShareLinkModal({ file, onClose }: Props) {
  const [mode, setMode] = useState<LinkMode>('permanent');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<api.FileLinkResult | null>(null);
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    setError('');
    setGenerating(true);
    try {
      const days = mode === 'temporary' ? TEMPORARY_DAYS : null;
      const link = await api.createFileLink(file.path, days);
      setResult(link);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo crear el enlace');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = result.url;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
      document.body.removeChild(ta);
    }
  };

  const formatExpiry = (iso: string | null) => {
    if (!iso) return 'Sin expiración';
    const d = new Date(iso.replace(' ', 'T') + 'Z');
    return d.toLocaleString();
  };

  return (
    <div
      className="modal show d-block feebbo-modal"
      tabIndex={-1}
      style={{ backgroundColor: 'rgba(0,0,20,0.7)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-share me-2" />
              Compartir archivo
            </h5>
            <button className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            <div className="mb-3">
              <label className="form-label small text-muted">Archivo</label>
              <div className="d-flex align-items-center gap-2 p-2 rounded" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <i className="bi bi-file-earmark" />
                <span className="text-truncate" title={file.path}>{file.name}</span>
              </div>
            </div>

            {!result && (
              <>
                <label className="form-label fw-medium mb-2" style={{ color: 'var(--feebbo-muted)' }}>
                  Tipo de enlace
                </label>
                <div className="d-flex flex-column gap-2 mb-3">
                  <label
                    className={`p-3 rounded border d-flex align-items-start gap-2 ${mode === 'permanent' ? 'border-primary' : ''}`}
                    style={{ cursor: 'pointer', background: mode === 'permanent' ? '#eff6ff' : '#fff' }}
                  >
                    <input
                      type="radio"
                      className="form-check-input mt-1"
                      checked={mode === 'permanent'}
                      onChange={() => setMode('permanent')}
                      disabled={generating}
                    />
                    <div>
                      <div className="fw-medium">
                        <i className="bi bi-shield-lock me-1" />
                        Permanente (privado)
                      </div>
                      <div className="small text-muted">
                        Enlace sin expiración. Requiere inicio de sesión para acceder al archivo.
                      </div>
                    </div>
                  </label>

                  <label
                    className={`p-3 rounded border d-flex align-items-start gap-2 ${mode === 'temporary' ? 'border-primary' : ''}`}
                    style={{ cursor: 'pointer', background: mode === 'temporary' ? '#eff6ff' : '#fff' }}
                  >
                    <input
                      type="radio"
                      className="form-check-input mt-1"
                      checked={mode === 'temporary'}
                      onChange={() => setMode('temporary')}
                      disabled={generating}
                    />
                    <div className="flex-grow-1">
                      <div className="fw-medium">
                        <i className="bi bi-clock-history me-1" />
                        Temporal (público)
                      </div>
                      <div className="small text-muted">
                        Cualquier persona con el enlace puede acceder durante <strong>{TEMPORARY_DAYS} días</strong>. Pasado ese plazo, el enlace dejará de funcionar.
                      </div>
                    </div>
                  </label>
                </div>

                {error && (
                  <div className="alert alert-danger py-2 small mb-0">
                    <i className="bi bi-exclamation-triangle me-1" />
                    {error}
                  </div>
                )}
              </>
            )}

            {result && (
              <div>
                <label className="form-label small text-muted">Enlace generado</label>
                <div className="input-group mb-2">
                  <input
                    type="text"
                    readOnly
                    className="form-control feebbo-input"
                    value={result.url}
                    onFocus={e => e.currentTarget.select()}
                  />
                  <button className="btn btn-feebbo" onClick={handleCopy} type="button">
                    <i className={`bi ${copied ? 'bi-check2' : 'bi-clipboard'} me-1`} />
                    {copied ? 'Copiado' : 'Copiar'}
                  </button>
                </div>
                <div className="small text-muted">
                  <i className="bi bi-info-circle me-1" />
                  {result.expiresAt
                    ? <>Expira: <strong>{formatExpiry(result.expiresAt)}</strong> · Acceso público</>
                    : <>Sin expiración · Requiere inicio de sesión</>
                  }
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            {!result ? (
              <>
                <button type="button" className="btn btn-feebbo-outline" onClick={onClose} disabled={generating}>
                  Cancelar
                </button>
                <button type="button" className="btn btn-feebbo" onClick={handleGenerate} disabled={generating}>
                  {generating
                    ? <><span className="spinner-border spinner-border-sm me-2" />Generando…</>
                    : <><i className="bi bi-link-45deg me-1" />Generar enlace</>
                  }
                </button>
              </>
            ) : (
              <>
                <button type="button" className="btn btn-feebbo-outline" onClick={() => { setResult(null); setCopied(false); }}>
                  <i className="bi bi-arrow-left me-1" />
                  Crear otro
                </button>
                <button type="button" className="btn btn-feebbo" onClick={onClose}>
                  Cerrar
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
